import { ColumnDef } from '@tanstack/react-table';
import { VirtualizedTable } from '@round/creatorbase-ui';
import { ActionsCell } from './components/ActionsCell/ActionsCell';
import { CreatorCell } from './components/CreatorCell/CreatorCell';
import { AvgViewsCell, EngagementRateCell, FollowersCell } from './components/StatCells/StatCells';
import { CreatorAccountRow, Meta } from './helpers';
import styles from './CreatorAccountsTable.module.css';

// Tall enough for the account's image in the creator cell, plus the cell's padding and the row's border.
// Rows are measured once rendered, so this only has to be close.
const ROW_HEIGHT_PX = 57;

// Narrower than Creator Explore's stat columns, which still holds a formatted stat, so the creator and
// the actions beside them fit the modal.
const STAT_COLUMN_SIZE = 80;

/**
 * Each creator is shown as its account, with the account's stats. The minimums add up to what the modal
 * has room for, so the table only ever scrolls down.
 */
const columns: ColumnDef<CreatorAccountRow>[] = [
    {
        header: 'Creator',
        id: 'creator',
        cell: CreatorCell,
        meta: { className: styles.creatorCell },
        size: 220,
    },
    {
        header: 'Followers',
        id: 'total_follower_count',
        cell: FollowersCell,
        size: STAT_COLUMN_SIZE,
    },
    {
        header: 'Avg. Views',
        id: 'avg_video_views_per_post',
        cell: AvgViewsCell,
        size: STAT_COLUMN_SIZE,
    },
    {
        header: 'ER%',
        id: 'avg_engagement_rate',
        cell: EngagementRateCell,
        size: STAT_COLUMN_SIZE,
    },
    {
        header: '',
        id: 'actions',
        cell: ActionsCell,
        meta: { className: styles.actionsCell },
        size: 180,
    },
];

type Props = {
    rows: CreatorAccountRow[];
    isExploreLoading: boolean;
    isImagesLoading: boolean;
};

/** Creator accounts in the order they were given, each of which can be put on a campaign from its row. */
const CreatorAccountsTable = ({ rows, isExploreLoading, isImagesLoading }: Props) => {
    const meta: Meta = { isExploreLoading, isImagesLoading };

    return (
        <VirtualizedTable
            columns={columns}
            data={rows}
            meta={meta}
            virtualizerOptions={{
                estimateSize: () => ROW_HEIGHT_PX,
                getItemKey: (index) => rows[index]?.key ?? index,
            }}
            containerClassName={styles.tableContainer}
        />
    );
};

export default CreatorAccountsTable;
